import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { ContextBar } from "../components/ContextBar";
import { ProductResultCard } from "../components/ProductResultCard";
import { EmptyState } from "../components/MoneyStat";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, erroMsg } from "../lib/api";
import { brl } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useMeta } from "../context/MetaContext";
import { PackageSearch, RefreshCw, Search, ShoppingCart, X } from "lucide-react";

export default function Consulta() {
  const { uf, enquadramento, adicionar, calculo, quantidadeItens } = useCart();
  const { ncms, origens, ufNome, enqNome } = useMeta();
  const navigate = useNavigate();

  const [busca, setBusca] = useState("");
  const [debounced, setDebounced] = useState("");
  const [ncmFiltro, setNcmFiltro] = useState("todos");
  const [origemFiltro, setOrigemFiltro] = useState("todas");
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [limite, setLimite] = useState(24);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(busca), 300);
    return () => clearTimeout(t);
  }, [busca]);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const r = await api.get("/products", {
        params: {
          q: debounced,
          ncm: ncmFiltro === "todos" ? "" : ncmFiltro,
          origem: origemFiltro === "todas" ? "" : origemFiltro,
          uf,
          enquadramento,
          desconto: 0,
          limit: limite,
        },
      });
      setItens(r.data.itens);
      setTotal(r.data.total);
    } catch (e) {
      setErro(erroMsg(e, "Não foi possível carregar os produtos."));
    } finally {
      setCarregando(false);
    }
  }, [debounced, ncmFiltro, origemFiltro, uf, enquadramento, limite]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const onAdicionar = (p) => {
    adicionar(p, 1);
    toast.success("Adicionado ao orçamento", {
      description: `${p.descricao_produto} — aplique o desconto no orçamento`,
      action: { label: "Ver orçamento", onClick: () => navigate("/orcamento") },
    });
  };

  const resumoContexto = useMemo(
    () => `${ufNome(uf)} • ${enqNome(enquadramento)} • preços de tabela com impostos`,
    [uf, enquadramento, ufNome, enqNome]
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <AppHeader />

      {/* contexto fiscal */}
      <div className="sky-wash border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h1 className="font-display text-[19px] font-bold tracking-tight text-slate-900">
                Consulta de preços
              </h1>
              <p className="text-[12.5px] text-slate-600">{resumoContexto}</p>
            </div>
            <Badge variant="secondary" className="bg-slate-900 text-white">
              {total} produto{total === 1 ? "" : "s"} disponíve{total === 1 ? "l" : "is"}
            </Badge>
          </div>
          <ContextBar />
          <p className="mt-2.5 text-[11.5px] text-slate-500">
            Os valores abaixo são de <b>tabela cheia</b>, já com IPI e ICMS-ST/DIFAL do estado e
            enquadramento selecionados. O <b>desconto é negociado item por item no orçamento</b>.
          </p>
        </div>
      </div>

      {/* busca + filtros */}
      <div className="sticky top-14 z-30 border-b border-slate-200 bg-white/95 backdrop-blur md:top-14">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:px-6">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              ref={inputRef}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por descrição, código ou NCM…"
              className="h-11 bg-white pl-9 pr-9"
              data-testid="consulta-busca-input"
            />
            {busca && (
              <button
                onClick={() => {
                  setBusca("");
                  inputRef.current?.focus();
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                data-testid="consulta-limpar-busca"
                aria-label="Limpar busca"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Select value={ncmFiltro} onValueChange={setNcmFiltro}>
              <SelectTrigger className="h-11 w-[150px] bg-white" data-testid="consulta-ncm-filtro">
                <SelectValue placeholder="NCM" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="todos">Todos os NCMs</SelectItem>
                {ncms.map((n) => (
                  <SelectItem key={n.codigo} value={n.codigo}>
                    {n.codigo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={origemFiltro} onValueChange={setOrigemFiltro}>
              <SelectTrigger className="h-11 w-[145px] bg-white" data-testid="consulta-origem-filtro">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="todas">Toda origem</SelectItem>
                {origens.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o === "IMPORTAÇÃO" ? "Importação" : "Fabricação"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* resultados */}
      <main className="mx-auto max-w-7xl px-3 py-5 sm:px-6">
        {erro && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-red-50 px-4 py-3 text-sm text-destructive">
            <span>{erro}</span>
            <Button size="sm" variant="outline" onClick={buscar} data-testid="consulta-tentar-novamente">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Tentar novamente
            </Button>
          </div>
        )}

        {carregando ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="mt-2 h-3 w-1/2" />
                <Skeleton className="mt-4 h-24 w-full" />
                <Skeleton className="mt-3 h-10 w-full" />
              </div>
            ))}
          </div>
        ) : itens.length === 0 ? (
          <EmptyState
            icone={PackageSearch}
            titulo="Nenhum produto encontrado"
            descricao="Tente buscar pelo código do produto (ex.: A0000000000), pelo NCM ou por parte da descrição. Produtos sem preço cadastrado não aparecem nesta lista."
            acao={
              <Button
                variant="outline"
                onClick={() => {
                  setBusca("");
                  setNcmFiltro("todos");
                  setOrigemFiltro("todas");
                }}
                data-testid="consulta-limpar-filtros"
              >
                Limpar filtros
              </Button>
            }
          />
        ) : (
          <>
            <div
              className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
              data-testid="consulta-resultados-list"
            >
              {itens.map((p, i) => (
                <ProductResultCard key={p.id} produto={p} index={i} onAdicionar={onAdicionar} />
              ))}
            </div>
            {itens.length < total && (
              <div className="mt-5 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setLimite((l) => l + 24)}
                  data-testid="consulta-carregar-mais"
                >
                  Carregar mais ({total - itens.length} restantes)
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* barra inferior do orçamento */}
      {quantidadeItens > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white shadow-[0_-6px_20px_rgba(15,23,42,0.08)]">
          <div
            className="mx-auto flex max-w-7xl items-center gap-3 px-3 py-2.5 sm:px-6"
            data-testid="consulta-orcamento-bottom-bar"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Orçamento em andamento • {quantidadeItens} item{quantidadeItens === 1 ? "" : "s"}
              </div>
              <div className="num font-display text-[21px] font-bold leading-tight text-slate-900">
                {brl(calculo?.totais?.total_geral)}
              </div>
            </div>
            <Button
              className="h-11 px-5"
              onClick={() => navigate("/orcamento")}
              data-testid="consulta-ver-orcamento-button"
            >
              <ShoppingCart className="mr-2 h-4 w-4" /> Ver orçamento
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
